if (!customElements.get('rf-outfit-selector')) {
  customElements.define('rf-outfit-selector', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-outfit-form]');
      this.addButton = this.querySelector('[data-outfit-add]');
      this.status = this.querySelector('[data-outfit-status]');
      this.labels = JSON.parse(this.querySelector('[data-outfit-labels]').textContent);
      this.rows = [...this.querySelectorAll('[data-outfit-row]')].map(node => {
        const row = JSON.parse(node.textContent);
        row.products = [...new Map(row.products.map(product => [product.handle, product])).values()];
        return { ...row, handle: row.base, variant: '', included: true };
      });
      this.root = window.Shopify?.routes?.root || '/';
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.querySelector('[data-outfit-open]').hidden = false;
      this.addEventListener('click', event => {
        if (event.target.closest('[data-outfit-open]')) this.open();
        if (event.target.closest('[data-outfit-close]') && !this.busy) this.dialog.close();
      }, { signal });
      this.dialog.addEventListener('cancel', event => { if (this.busy) event.preventDefault(); }, { signal });
      this.form.addEventListener('submit', event => { event.preventDefault(); this.add(); }, { signal });
      this.dialog.addEventListener('change', event => this.change(event), { signal });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.ready = false;
      if (this.dialog?.open) this.dialog.close();
    }
    text(tag, text, className) {
      const element = document.createElement(tag);
      if (text) element.textContent = text;
      if (className) element.className = className;
      return element;
    }
    product(row) { return row.products.find(product => product.handle === row.handle); }
    variant(row) { return this.product(row)?.variants.find(variant => String(variant.id) === row.variant); }
    usable(product, variant) {
      const rule = variant?.quantity_rule;
      return Boolean(variant?.available && !product.requires_selling_plan && (!rule || (rule.min <= 1 && rule.increment <= 1 && (rule.max === null || rule.max >= 1))));
    }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: this.dataset.currency || window.Shopify?.currency?.active || 'EUR' }).format(cents / 100);
    }
    async open() {
      if (this.busy) return;
      this.needsReview = false;
      this.status.textContent = '';
      const selected = document.querySelector(`form[id^="product-form-main-${this.dataset.productId}-"] input[name="id"]`)?.value;
      if (selected && this.rows[0].products.find(product => product.handle === this.rows[0].handle)?.variants.some(variant => String(variant.id) === selected)) this.rows[0].variant = selected;
      this.render();
      this.dialog.showModal();
      this.status.textContent = this.labels.checking;
      this.busy = true;
      this.update();
      try {
        await this.refresh(this.rows);
        this.render();
        this.status.textContent = '';
      } catch {
        this.status.textContent = this.labels.load_error;
        this.needsReview = true;
      } finally {
        this.busy = false;
        this.update();
      }
    }
    render() {
      const active = document.activeElement;
      const focusedRow = this.contains(active) ? active.closest('[data-row]')?.dataset.row : null;
      const focusedAction = active?.dataset?.action;
      const container = this.querySelector('[data-outfit-items]');
      container.replaceChildren();
      this.rows.forEach((row, index) => {
        const product = this.product(row);
        const article = this.text('article', '', 'rf-outfit-item');
        article.dataset.row = String(index);
        const image = document.createElement('img');
        image.alt = '';
        image.width = 84;
        image.height = 105;
        image.loading = 'lazy';
        if (product.image) image.src = product.image;
        else image.hidden = true;
        article.append(image);
        const fields = this.text('div', '', 'rf-outfit-item__fields');
        const includeLabel = this.text('label', '', 'rf-outfit-item__include');
        const include = document.createElement('input');
        include.type = 'checkbox';
        include.dataset.action = 'include';
        include.checked = row.included;
        includeLabel.append(include, this.text('span', product.title));
        fields.append(includeLabel);
        if (row.products.length > 1) {
          const label = this.text('label', '', 'rf-outfit-item__label');
          label.append(this.text('span', this.labels.colour));
          const select = document.createElement('select');
          select.dataset.action = 'colour';
          select.disabled = !row.included;
          row.products.forEach(item => {
            const available = item.variants.some(variant => this.usable(item, variant));
            const option = new Option(item.title + (available ? '' : ` — ${this.labels.sold_out}`), item.handle);
            option.disabled = !available;
            option.selected = item.handle === row.handle;
            select.add(option);
          });
          label.append(select);
          fields.append(label);
        }
        const label = this.text('label', '', 'rf-outfit-item__label');
        const sizeName = product.options?.length === 1 ? (product.options[0].name || product.options[0]) : this.labels.option;
        label.append(this.text('span', sizeName || this.labels.option));
        const select = document.createElement('select');
        select.dataset.action = 'variant';
        select.required = row.included;
        select.disabled = !row.included;
        const placeholder = new Option(this.labels.choose, '');
        placeholder.selected = !row.variant;
        select.add(placeholder);
        product.variants.forEach(variant => {
          const available = this.usable(product, variant);
          const option = new Option(variant.title + (available ? '' : ` — ${this.labels.sold_out}`), String(variant.id));
          option.disabled = !available;
          option.selected = available && String(variant.id) === row.variant;
          select.add(option);
        });
        if (!this.usable(product, this.variant(row))) { row.variant = ''; select.value = ''; }
        label.append(select);
        fields.append(label);
        const price = this.variant(row)?.price ?? product.variants.find(variant => this.usable(product, variant))?.price;
        fields.append(this.text('p', price === undefined ? this.labels.sold_out : this.money(price), 'rf-outfit-item__price'));
        article.append(fields);
        container.append(article);
      });
      this.update();
      if (focusedRow !== null && focusedAction) this.querySelector(`[data-row="${focusedRow}"] [data-action="${focusedAction}"]`)?.focus({ preventScroll: true });
    }
    update() {
      const selected = this.rows.filter(row => row.included);
      const complete = selected.length > 0 && selected.every(row => this.usable(this.product(row), this.variant(row)));
      const total = selected.reduce((sum, row) => sum + (this.variant(row)?.price || 0), 0);
      this.addButton.disabled = this.busy || this.needsReview || !complete;
      this.addButton.setAttribute('aria-busy', String(Boolean(this.busy)));
      this.addButton.textContent = this.busy ? this.labels.checking : this.labels.add + (complete ? ` · ${this.money(total)}` : '');
      this.querySelector('[data-outfit-close]').disabled = Boolean(this.busy);
      this.querySelectorAll('[data-outfit-items] input, [data-outfit-items] select').forEach(control => {
        const row = this.rows[Number(control.closest('[data-row]').dataset.row)];
        control.disabled = Boolean(this.busy) || (control.tagName === 'SELECT' && !row.included);
      });
    }
    async change(event) {
      const control = event.target;
      const parent = control.closest('[data-row]');
      if (!parent || this.busy) return;
      const row = this.rows[Number(parent.dataset.row)];
      this.status.textContent = '';
      if (control.dataset.action === 'include') { row.included = control.checked; this.render(); }
      if (control.dataset.action === 'variant') { row.variant = control.value; this.render(); }
      if (control.dataset.action === 'colour') {
        row.handle = control.value;
        row.variant = '';
        this.busy = true;
        this.render();
        try { await this.refresh([row]); this.needsReview = false; }
        catch { this.status.textContent = this.labels.load_error; this.needsReview = true; }
        finally { this.busy = false; this.render(); }
      }
    }
    async json(url, options) {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || this.labels.load_error);
      return data;
    }
    async refresh(rows) {
      const handles = [...new Set(rows.map(row => row.handle))];
      const products = await Promise.all(handles.map(handle => this.json(`${this.root}products/${encodeURIComponent(handle)}.js`)));
      products.forEach(fresh => {
        this.rows.forEach(row => {
          const old = row.products.find(product => product.handle === fresh.handle);
          if (!old) return;
          const variants = fresh.variants.map(variant => ({ ...variant, quantity_rule: variant.quantity_rule || old.variants.find(item => item.id === variant.id)?.quantity_rule }));
          Object.assign(old, { title: fresh.title, variants, requires_selling_plan: fresh.requires_selling_plan ?? old.requires_selling_plan });
        });
      });
    }
    async add() {
      if (this.busy || this.needsReview || this.addButton.disabled) return;
      this.busy = true;
      this.status.textContent = '';
      this.update();
      let attempted = false;
      try {
        const selected = this.rows.filter(row => row.included);
        await this.refresh(selected);
        if (!selected.every(row => this.usable(this.product(row), this.variant(row)))) {
          selected.forEach(row => { if (!this.usable(this.product(row), this.variant(row))) row.variant = ''; });
          this.render();
          this.status.textContent = this.labels.stock_changed;
          return;
        }
        const quantities = new Map();
        selected.forEach(row => quantities.set(row.variant, (quantities.get(row.variant) || 0) + 1));
        const items = [...quantities].map(([id, quantity]) => ({ id: Number(id), quantity }));
        await this.json(`${this.root}cart.js`);
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        attempted = true;
        const result = await this.json(`${this.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: [...new Set(sections)], sections_url: location.pathname })
        });
        const cart = await this.json(`${this.root}cart.js`);
        cart.sections = result.sections || {};
        this.dialog.close();
        this.rows.forEach(row => { row.variant = ''; });
        if (window.themeVariables?.settings?.cartType === 'page') { location.href = `${this.root}cart`; return; }
        const bundled = sections.length > 0 && sections.every(id => typeof cart.sections[id] === 'string');
        if (bundled) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart } }));
        } else {
          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.addEventListener('cart-drawer:refreshed', () => drawer.show(), { once: true });
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          if (!drawer) location.href = `${this.root}cart`;
        }
      } catch (error) {
        this.needsReview = attempted;
        this.status.textContent = attempted ? `${error.message} ${this.labels.check_cart}` : this.labels.load_error;
        if (attempted) document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        this.busy = false;
        this.update();
      }
    }
  });
}
